import React, { useState, useEffect } from 'react';
import { 
  Box,
  Typography,
  IconButton,
  Card,
  CardContent,
  Chip,
  List,
  ListItem,
  ListItemText,
  Button,
} from '@mui/material';
import {
  QrCode2 as QrCodeIcon,
  Keyboard as KeyboardIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import QRScanner from './QRScanner';
import { useNavigate } from 'react-router-dom';
import { userCouponlist } from '../../api/modules/couponTemplate';
import { showMessage } from '../../utils';
import { isInApp } from '../../utils/environment';

const Verification = () => {
  const [activeTab, setActiveTab] = useState('scan');
  const [showScanner, setShowScanner] = useState(false);
  const navigate = useNavigate();
  const [pendingOrders, setPendingOrders] = useState([]);
  const [verificationHistory, setVerificationHistory] = useState([]);
  const [pendingTotal, setPendingTotal] = useState(0);
  const [historyTotal, setHistoryTotal] = useState(0);

  useEffect(() => {
    fetchPendingOrders();
    fetchVerificationHistory();
  }, []);

  const fetchPendingOrders = async () => {
    try {
      const params = {
        pageSize: 2,
        pageNum: 1,
        bizType: 2
      };
      
      await userCouponlist(params, (result) => {
        const { code, rows, total } = result;
        if (code === 200 && rows) {
          setPendingOrders(rows);
          setPendingTotal(total);
        }
      });
    } catch (error) {
      showMessage('Failed to fetch pending orders', 'error');
    }
  };

  const fetchVerificationHistory = async () => {
    try {
      const params = {
        pageSize: 2,
        pageNum: 1,
        bizType: 1
      };
      
      await userCouponlist(params, (result) => {
        const { code, rows, total } = result;
        if (code === 200 && rows) {
          setVerificationHistory(rows);
          setHistoryTotal(total);
        }
      });
    } catch (error) {
      showMessage('Failed to fetch verification history', 'error');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      hour12: true
    });
  };

  const VerificationOption = ({ icon, title, description, onClick }) => (
    <motion.div
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
    >
      <Card 
        onClick={onClick}
        sx={{
          boxShadow: 'none'
        }}
      >
        <CardContent sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          textAlign: 'center',
          p: 3
        }}>
          {React.cloneElement(icon, { 
            sx: { 
              fontSize: 32, 
              color: '#007AFF',
              mb: 1.5
            }
          })}
          <Typography sx={{ 
            fontWeight: 900,
            mb: 0.5,
            fontSize: '16px',
            color: '#000000'
          }}>
            {title}
          </Typography>
          <Typography sx={{ 
            color: '#6B7280',
            fontSize: '14px',
            fontWeight: 400
          }}>
            {description}
          </Typography>
        </CardContent>
      </Card>
    </motion.div>
  );

  const handleScanSuccess = async (result) => {
    setShowScanner(false);
  };

  return (
    <Box sx={{ maxWidth: 600, mx: 'auto', p: 2, bgcolor: '#F8F9FA', height: '100vh' }}>
      {/* Header */}
      {
        !isInApp() && (
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            mb: 3,
            pt: 1
          }}>
        <IconButton 
          edge="start" 
          sx={{ mr: 2 }}
          onClick={() => navigate(-1)}
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography sx={{ 
          fontWeight: 600,
          fontSize: '1.25rem',
          color: '#111111'
        }}>
          Verification
        </Typography>
      </Box>
        )
      }

      {/* Verification Options */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <Box sx={{ flex: 1,bgcolor: '#fff',
          borderRadius: '16px',
          width: '100%',
          border: '1px solid',
          borderColor: 'rgba(0, 0, 0, 0.06)',
          boxShadow: 'none' }}>
          <VerificationOption
            icon={<QrCodeIcon />}
            title="Scan QR Code"
            description="Scan customer's QR code"
            onClick={() => {
              setActiveTab('scan');
              setShowScanner(true);
            }}
          />
        </Box>
        <Box sx={{ flex: 1, bgcolor: '#fff',
          borderRadius: '16px',
          width: '100%',
          border: '1px solid',
          borderColor: 'rgba(0, 0, 0, 0.06)',
          boxShadow: 'none' }}>
          <VerificationOption
            icon={<KeyboardIcon />}
            title="Enter Code"
            description="Type verification code"
            onClick={() => {
              setActiveTab('enter');
              navigate('/campaigns/invitations/enter-code');
            }}
          />
        </Box>
      </Box>

      {/* Pending Orders Card */}
      <Card sx={{ 
        mb: 3,
        borderRadius: '16px',
        boxShadow: 'none',
        border: '1px solid',
        borderColor: 'rgba(0, 0, 0, 0.06)'
      }}>
        <CardContent sx={{ p: 2 }}>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            mb: 2 
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ 
                fontWeight: 600,
                fontSize: '1.125rem',
                color: '#111111'
              }}>
                Pending Orders
              </Typography>
              <Chip 
                label={pendingTotal} 
                size="small"
                sx={{ 
                  bgcolor: '#0066FF',
                  color: 'white',
                  height: 24,
                  fontSize: '0.75rem',
                  fontWeight: 500
                }}
              />
            </Box>
            <Typography 
              onClick={() => navigate('/campaigns/invitations/pending-orders')}
              sx={{ 
                color: '#0066FF',
                fontSize: '0.875rem',
                fontWeight: 500,
                cursor: 'pointer'
              }}
            >
              View All
            </Typography>
          </Box>

          <List sx={{ p: 0 }}>
            {pendingOrders.length > 0 ? (
              pendingOrders.map((order) => (
                <ListItem key={order.couponId} sx={{ 
                  bgcolor: '#F8F9FA',
                  borderRadius: '12px',
                  mb: 1,
                  p: 2
                }}>
                  <ListItemText
                    primary={
                      <Typography sx={{ 
                        fontWeight: 500,
                        color: '#111111',
                        fontSize: '0.9375rem'
                      }}>
                        Order #{order.couponId}
                      </Typography>
                    }
                    secondary={
                      <Typography sx={{ 
                        color: '#6B7280',
                        fontSize: '0.875rem',
                        mt: 0.5
                      }}>
                        {formatDate(order.createTime)}
                      </Typography>
                    }
                  />
                  <Typography sx={{ 
                    fontWeight: 600,
                    color: '#111111'
                  }}>
                    ${order.actualPrice}
                  </Typography>
                </ListItem>
              ))
            ) : (
              <Box sx={{ 
                textAlign: 'center',
                py: 3,
                color: '#6B7280'
              }}>
                No pending orders
              </Box>
            )}
          </List>
        </CardContent>
      </Card>

      {/* Verification History */}
      <Card sx={{ 
        mb: 3,
        borderRadius: '16px',
        boxShadow: 'none',
        border: '1px solid',
        borderColor: 'rgba(0, 0, 0, 0.06)'
      }}>
        <CardContent sx={{ p: 2 }}>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            mb: 2 
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ 
                fontWeight: 600,
                fontSize: '1.125rem',
                color: '#111111'
              }}>
                Verification History
              </Typography>
              <Chip 
                label={historyTotal} 
                size="small"
                sx={{ 
                  bgcolor: '#0066FF',
                  color: 'white',
                  height: 24,
                  fontSize: '0.75rem',
                  fontWeight: 500
                }}
              />
            </Box>
            <Typography 
              onClick={() => navigate('/campaigns/invitations/verification-history')}
              sx={{ 
                color: '#0066FF',
                fontSize: '0.875rem',
                fontWeight: 500,
                cursor: 'pointer'
              }}
            >
              View All
            </Typography>
          </Box>

          <List sx={{ p: 0 }}>
            {verificationHistory.length > 0 ? (
              verificationHistory.map((record) => (
                <ListItem key={record.couponId} sx={{ 
                  bgcolor: '#F8F9FA',
                  borderRadius: '12px',
                  mb: 1,
                  p: 2
                }}>
                  <ListItemText
                    primary={
                      <Typography sx={{ 
                        fontWeight: 500,
                        color: '#111111',
                        fontSize: '0.9375rem'
                      }}>
                        Order #{record.couponId}
                      </Typography>
                    }
                    secondary={
                      <Typography sx={{ 
                        color: '#6B7280',
                        fontSize: '0.875rem',
                        mt: 0.5
                      }}>
                        {formatDate(record.createTime)}
                      </Typography>
                    }
                  />
                </ListItem>
              ))
            ) : (
              <Box sx={{ 
                textAlign: 'center',
                py: 3,
                color: '#6B7280'
              }}>
                No verification history
              </Box>
            )}
          </List>
        </CardContent>
      </Card>

      {/* Export Button */}
      {/* <Button
        variant="contained"
        fullWidth
        sx={{ 
          bgcolor: '#111111',
          color: 'white',
          borderRadius: '100px',
          py: 1.5,
          textTransform: 'none',
          fontSize: '1rem',
          fontWeight: 500,
          '&:hover': {
            bgcolor: '#000000'
          }
        }}
      >
        Export Records
      </Button> */}

      {/* QR Scanner Modal */}
      {showScanner && (
        <QRScanner
          onClose={() => setShowScanner(false)}
          onScanSuccess={handleScanSuccess}
        />
      )}
    </Box>
  );
};

export default Verification;
